import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchArticles } from "../lib/api";

const AUTOPLAY_MS = 3000;

// Cinematic crossfade + Ken Burns zoom (no hard scroll)
const slideVariants = {
  enter: { opacity: 0, scale: 1.12 },
  center: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.05 },
};

export default function Home() {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    fetchArticles({ featured: true }).then(setSlides).catch(() => {});
  }, []);

  const next = useCallback(() => {
    setIndex((i) => (slides.length ? (i + 1) % slides.length : 0));
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timer.current);
  }, [slides.length, next]);

  if (!slides.length) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-ink">
        <span className="font-logo text-5xl text-white animate-pulse">Phusic</span>
      </div>
    );
  }

  const current = slides[index];

  return (
    <section
      data-testid="home-slider"
      className="relative h-screen w-full overflow-hidden bg-ink"
    >
      {/* Pure full-screen images */}
      <AnimatePresence initial={false} mode="sync">
        <motion.div
          key={current.slug}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            opacity: { duration: 1.1, ease: [0.4, 0, 0.2, 1] },
            scale: { duration: 5, ease: [0.22, 1, 0.36, 1] },
          }}
          className="absolute inset-0"
        >
          <img
            src={current.hero_image}
            alt={current.title}
            className="h-full w-full object-cover"
          />
          {/* Very light scrim only for logo legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Click anywhere on the image to enter the site / open the story */}
      <button
        data-testid="slide-open-article"
        aria-label={`Open ${current.title}`}
        onClick={() => navigate(`/article/${current.slug}`)}
        className="absolute inset-0 z-10 cursor-pointer"
      />

      {/* Fixed cursive logo bottom-left — the only mark on screen */}
      <div className="absolute z-40 bottom-8 left-8">
        <span
          data-testid="phusic-home-logo"
          className="font-logo text-5xl sm:text-6xl text-white leading-none"
          style={{ textShadow: "0 2px 30px rgba(0,0,0,0.8)" }}
        >
          Phusic
        </span>
      </div>
    </section>
  );
}
